import React, { useEffect, useState } from 'react';
import { Inbox, CalendarClock, Handshake, AlertTriangle, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { StatusBadge, fmtDT, leadName, isWalkIn, AssignAgentDialog, AssignSalespersonDialog, LeadDetailDialog } from './BdcShared';
import { useAuth } from '../context/AuthContext';

const Section = ({ icon: Icon, title, color, items, empty, renderAction, onOpen, testid }) => (
  <div className="rounded-xl border border-border/70 bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <p className="font-display font-black text-sm uppercase tracking-wide flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} /> {title}
      </p>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-muted/50 ${items.length > 0 ? color : 'text-muted-foreground/60'}`}>{items.length}</span>
    </div>
    {items.length === 0 ? <p className="text-sm text-muted-foreground/60 py-4 text-center">{empty}</p> : (
      <div className="space-y-2 max-h-[340px] overflow-y-auto">
        {items.map((l) => (
          <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/70 hover:border-foreground/[0.15] transition-colors cursor-pointer" onClick={() => onOpen(l)}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{leadName(l)} <StatusBadge status={l.status} /></p>
              <p className="text-[11px] text-muted-foreground truncate">
                {l.phone || l.email} · {l.vehicle_interest || 'Sem veículo'} · {l.source}
                {l.appointment_date ? ` · Appt: ${l.appointment_date} ${l.appointment_time}` : ''}
                {l.bdc_agent_name ? ` · Agent: ${l.bdc_agent_name}` : ''}
              </p>
              <p className="text-[10px] text-muted-foreground/60">{fmtDT(l.received_at)}</p>
            </div>
            <div onClick={(e) => e.stopPropagation()}>{renderAction && renderAction(l)}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const ManagerQueue = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState({ new_leads: [], awaiting_sales: [], salesperson_requests: [], attention_needed: [] });
  const [assignAgentLead, setAssignAgentLead] = useState(null);
  const [assignSpLead, setAssignSpLead] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/bdc/manager-queue'); setQueue(data); } catch (e) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const assignAgentBtn = (l) => (
    <button data-testid={`q-assign-agent-${l.id}`} onClick={() => setAssignAgentLead(l)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:text-foreground border border-primary/40 hover:border-primary rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"><UserPlus className="w-3.5 h-3.5" /> Assign Agent</button>
  );
  const assignSpBtn = (l) => (
    <button data-testid={`q-assign-sp-${l.id}`} onClick={() => setAssignSpLead(l)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400 hover:text-foreground border border-emerald-500/40 hover:border-emerald-500 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"><Handshake className="w-3.5 h-3.5" /> Assign Salesperson</button>
  );
  const newLeadActions = (l) => (
    <div className="flex flex-col sm:flex-row gap-1.5">
      {assignAgentBtn(l)}
      {isWalkIn(l) && (
        <button data-testid={`q-direct-sp-${l.id}`} onClick={() => setAssignSpLead(l)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400 hover:text-foreground border border-emerald-500/40 hover:border-emerald-500 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"><Handshake className="w-3.5 h-3.5" /> Direto p/ Vendedor</button>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="manager-queue">
      <Section icon={Inbox} title="New Leads" color="text-sky-400" items={queue.new_leads} empty="Nenhum lead aguardando distribuição." renderAction={newLeadActions} onOpen={(l) => setDetailId(l.id)} testid="queue-new-leads" />
      <Section icon={CalendarClock} title="Appointments Awaiting Sales Assignment" color="text-violet-400" items={queue.awaiting_sales} empty="Nenhum appointment aguardando vendedor." renderAction={assignSpBtn} onOpen={(l) => setDetailId(l.id)} testid="queue-awaiting-sales" />
      <Section icon={Handshake} title="Salesperson Requests" color="text-orange-400" items={queue.salesperson_requests} empty="Nenhuma solicitação de vendedor." renderAction={assignSpBtn} onOpen={(l) => setDetailId(l.id)} testid="queue-sp-requests" />
      <Section icon={AlertTriangle} title="Attention Needed" color="text-red-400" items={queue.attention_needed} empty="Nenhum lead precisando de intervenção." renderAction={assignAgentBtn} onOpen={(l) => setDetailId(l.id)} testid="queue-attention" />

      <AssignAgentDialog lead={assignAgentLead} open={!!assignAgentLead} onClose={() => setAssignAgentLead(null)} onDone={load} />
      <AssignSalespersonDialog lead={assignSpLead} open={!!assignSpLead} onClose={() => setAssignSpLead(null)} onDone={load} />
      <LeadDetailDialog leadId={detailId} open={!!detailId} onClose={() => setDetailId(null)} canAssignAgent={user?.permissions?.includes('assign_bdc_agent')} canAssignSalesperson={user?.permissions?.includes('assign_salesperson')} currentUserId={user?.user_id} onChanged={load} />
    </div>
  );
};
